import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { environment as env } from '@environments/environment';
import { Event } from '@models/events/event.model';
import { LegalInfoLinkType } from '@models/legal-info/enums/legal-info-link-type.enum';
import { SocialMediaLink } from '@models/social-media/social-media-link.model';
import { User } from '@models/users/user.model';
import { EditSocialMediaButtonsComponent } from '@modules/events/components/edit-social-media-buttons/edit-social-media-buttons.component';
import { LogoCustomFillComponent } from '@modules/shared/logo-custom-fill/logo-custom-fill.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { ScreenWidthService } from '@services/shared/screen-width.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    MatMenuModule,
    LogoCustomFillComponent,
    EditSocialMediaButtonsComponent,
  ],
  providers: [ScreenWidthService],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent {
  @Input() showLogo = false;
  @Input() logoUrl?: string;
  @Input() bodyColor?: string;
  @Input() backgroundColor?: string;
  @Input() showOptions?: boolean = true;
  @Input() includePoweredBy?: boolean = false;
  @Input() isEventFooter = false;
  @Input() isGenericFooter = false;
  @Input() isBookASlotFooter = false;
  @Input() isPublicFooter = false;
  @Input() event?: Event;
  currentYear: number = new Date().getFullYear();
  isDesktop?: boolean;
  isOver600?: boolean;
  loggedUser?: User;
  selectedLanguage: string = 'APP.LANGUAGE.ENGLISH';
  languageData = [
    {
      label: 'APP.LANGUAGE.ENGLISH',
      value: 'en',
    },
    {
      label: 'APP.LANGUAGE.GERMAN',
      value: 'de',
    },
  ];

  constructor(
    private screenWidthService: ScreenWidthService,
    private translateService: TranslateService,
    private authService: AuthService,
    private dateAdapter: DateAdapter<any>,
  ) {
    this.trackUserChanges();
    this.screenWidthService
      .isDesktop()
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => {
        this.isDesktop = isDesktop;
      });

    this.screenWidthService
      .isOver600()
      .pipe(takeUntilDestroyed())
      .subscribe((isOver600) => {
        this.isOver600 = isOver600;
      });

    const currentLang = this.languageData.find(
      (languaage) => languaage.value === this.translateService.currentLang,
    );

    if (currentLang) {
      this.selectedLanguage = currentLang.label;
    }
  }

  getContactName() {
    const items = [
      this.event?.eventTemplate?.legalInfo?.companyName,
      this.event?.eventTemplate?.legalInfo?.companyOffice,
    ];
    return items.filter(Boolean).join(' | ');
  }
  getCompanyAddress() {
    const items = [
      this.event?.eventTemplate?.legalInfo?.address,
      this.event?.eventTemplate?.legalInfo?.locality,
      this.event?.eventTemplate?.legalInfo?.country,
      this.event?.eventTemplate?.legalInfo?.postalCode,
    ];
    return items.filter(Boolean).join(', ');
  }

  getContactInfo() {
    const items = [
      this.event?.eventTemplate?.legalInfo?.email,
      this.event?.eventTemplate?.legalInfo?.phone,
    ];
    return items.filter(Boolean).join(' | ');
  }

  getItemStyle(): { [key: string]: string | number } {
    return {
      color: this.event?.eventTemplate?.visual?.footerBodyColor || 'inherit',
      opacity: 0.7,
    };
  }

  getLegalInfoDisplayName(type: LegalInfoLinkType) {
    if (type === LegalInfoLinkType.TERMS_AND_CONDITIONS) {
      return this.translateService.instant('APP.TERMS_AND_CONDITIONS_SHORT');
    } else return LegalInfoLinkType.getDisplayName(type);
  }
  haveSocialMediaLinks() {
    if (
      !this.event ||
      !this.event.eventTemplate ||
      !this.event.eventTemplate.socialMedia ||
      !this.event.useCustomFooter
    )
      return false;

    return this.event.eventTemplate.socialMedia.links.length > 0;
  }

  getFooterLogo() {
    if (
      this.event?.useCustomFooter &&
      this.logoUrl &&
      this.logoUrl.trim() !== ''
    ) {
      return this.logoUrl;
    } else {
      return this.userTheme === 'dark' ? env.logoDarkAsset : env.logoAsset;
    }
  }

  onMenuEvent(languageSelected: string) {
    this.selectedLanguage = languageSelected;

    const selectedLangData = this.languageData.find(
      (languaage) => languaage.label === this.selectedLanguage,
    );
    if (selectedLangData) {
      if (this.loggedUser && this.loggedUser.id > 0) {
        this.authService
          .updateUser({
            id: this.loggedUser.id,
            language: selectedLangData.value,
          })
          .subscribe(() =>
            localStorage.setItem('userLang', selectedLangData.value),
          );
      } else {
        localStorage.setItem('userLang', selectedLangData.value);
      }

      this.translateService.use(selectedLangData.value);
      this.dateAdapter.setLocale(selectedLangData.value);
    }
  }

  private trackUserChanges() {
    this.authService.userSubject
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        this.loggedUser = user;
      });
  }

  get userTheme(): string {
    const userTheme = localStorage.getItem('userTheme');

    if (userTheme) {
      return userTheme;
    }

    return 'light';
  }

  protected env = env;
  protected readonly SocialMediaLink = SocialMediaLink;
  protected readonly LegalInfoLinkType = LegalInfoLinkType;
}
